
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchManyToManyFunc,
	makeJoinEqualsFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserGroupEntity from 'Models/Security/Acl/UserGroupEntity';
import VisitorsGroupEntity from 'Models/Security/Acl/VisitorsGroupEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IGroupEntityAttributes extends IModelAttributes {
	name: string;

	customerss: Array<
		| Models.GroupsCustomers
		| Models.IGroupsCustomersAttributes
	>;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;

}

@entity('GroupEntity', 'Group')
export default class GroupEntity extends Model
	implements IGroupEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserGroupEntity(),
		new VisitorsGroupEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<GroupEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Customers',
		displayType: 'reference-multicombobox',
		order: 20,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.GroupsCustomers,
		optionEqualFunc: makeJoinEqualsFunc('customersId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'groupEntity',
			oppositeEntityName: 'customerEntity',
			relationName: 'groups',
			relationOppositeName: 'customers',
			entity: () => Models.GroupEntity,
			joinEntity: () => Models.GroupsCustomers,
			oppositeEntity: () => Models.CustomerEntity,
		}),
		
	})
	public customerss: Models.GroupsCustomers[] = [];

	@observable
	@attribute()
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.OrganisationEntity,
		
	})
	public organisationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IGroupEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IGroupEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.customerss !== undefined && Array.isArray(attributes.customerss)) {
				for (const model of attributes.customerss) {
					if (model instanceof Models.GroupsCustomers) {
						this.customerss.push(model);
					} else {
						this.customerss.push(new Models.GroupsCustomers(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		customerss {
			${Models.GroupsCustomers.getAttributes().join('\n')}
			customers {
				${Models.CustomerEntity.getAttributes().join('\n')}
			}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			customerss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'customerss',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(GroupEntity.prototype, 'created');
CRUD(modifiedAttr)(GroupEntity.prototype, 'modified');
